// Copyright ©️ 2026 eVolve MEP, LLC

import copySvg from 'assets/icons/icon-src/markup/copy.svg?raw';
import cutSvg from 'assets/icons/icon-src/markup/cut.svg?raw';
import deleteSvg from 'assets/icons/icon-src/markup/delete.svg?raw';
import duplicateSvg from 'assets/icons/icon-src/markup/duplicate.svg?raw';
import pasteSvg from 'assets/icons/icon-src/markup/paste.svg?raw';

const TOOLBAR_HEIGHT_PX = 36;
const TOOLBAR_MARGIN_PX = 22;
const ROTATION_HANDLE_PX = 26;

type ToolbarButton = {
  type: 'button';
  icon: string;
  title: string;
  onClick: () => void;
  className?: string;
};

type ToolbarSeparator = {
  type: 'separator';
};

type ToolbarElement = ToolbarButton | ToolbarSeparator;

export class SelectionToolbar {
  visible: boolean;
  private readonly viewerContainer: HTMLElement;
  private readonly markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore;
  readonly element: HTMLElement;

  constructor(viewerContainer: HTMLElement, markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore) {
    this.visible = false;
    this.viewerContainer = viewerContainer;
    this.markupExtension = markupExtension;
    this.element = this.createToolbar();
    viewerContainer.appendChild(this.element);
  }

  private createToolbar(): HTMLElement {
    const toolbar = document.createElement('div');
    toolbar.className = 'markup-selection-toolbar';

    const toolbarElements: ToolbarElement[] = [
      { type: 'button', icon: cutSvg, title: 'Cut', onClick: () => this.markupExtension.cut() },
      { type: 'button', icon: copySvg, title: 'Copy', onClick: () => this.markupExtension.copy() },
      { type: 'button', icon: pasteSvg, title: 'Paste', onClick: () => this.markupExtension.paste() },
      {
        type: 'button',
        icon: duplicateSvg,
        title: 'Duplicate',
        onClick: () => {
          const selectedMarkup = this.markupExtension.getSelection();
          if (selectedMarkup) {
            this.markupExtension.copy();
            this.markupExtension.paste();
          }
        },
      },
      { type: 'separator' },
      {
        type: 'button',
        icon: deleteSvg,
        title: 'Delete',
        className: 'delete',
        onClick: () => {
          const selectedMarkup = this.markupExtension.getSelection();
          if (selectedMarkup) {
            this.markupExtension.deleteMarkup(selectedMarkup);
          }
        },
      },
    ];

    toolbarElements.forEach((toolbarElement) => {
      if (toolbarElement.type === 'button') {
        toolbar.appendChild(this.createButton(toolbarElement));
      }
      if (toolbarElement.type === 'separator') {
        toolbar.appendChild(this.createSeparator());
      }
    });

    return toolbar;
  }

  private createButton({ icon, title, className, onClick }: ToolbarButton): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = title;
    if (className) button.className = className;
    button.innerHTML = icon;
    button.addEventListener('click', onClick);

    // Prevent the Autodesk viewer from stealing mouse / pointer events.
    for (const event of ['mousedown', 'mouseup', 'pointerdown', 'pointerup'] as const) {
      button.addEventListener(event, (e) => e.stopPropagation());
    }

    return button;
  }

  private createSeparator(): HTMLElement {
    const separator = document.createElement('div');
    separator.className = 'separator';

    return separator;
  }

  /** Displays the toolbar and positions it relative to the given markup. */
  show(markup: Autodesk.Viewing.Extensions.Markups.Core.Markup) {
    this.visible = true;
    this.reposition(markup);
  }

  /**
   * Recalculates the toolbar position relative to the given markup.
   * Prefers rendering above the markup but falls back to below if there is not enough room.
   * Always clamps its position to stay within the viewer container.
   * Hides the toolbar if the markup is completely off-screen.
   */
  reposition(markup: Autodesk.Viewing.Extensions.Markups.Core.Markup) {
    const markupRect = markup.getBoundingRect();
    const containerRect = this.viewerContainer.getBoundingClientRect();

    // Account for the markup's rotation by computing the bounding box of the rotated rectangle.
    const rotation = markup.getRotation();
    const centerX = markupRect.x + markupRect.width / 2;
    const centerY = markupRect.y + markupRect.height / 2;
    const halfWidth = markupRect.width / 2;
    const halfHeight = markupRect.height / 2;

    const cos = Math.abs(Math.cos(rotation));
    const sin = Math.abs(Math.sin(rotation));

    const rotatedHalfWidth = halfWidth * cos + halfHeight * sin;
    const rotatedHalfHeight = halfWidth * sin + halfHeight * cos;

    const markupLeft = centerX - rotatedHalfWidth;
    const markupTop = centerY - rotatedHalfHeight;
    const markupWidth = rotatedHalfWidth * 2;
    const markupHeight = rotatedHalfHeight * 2;

    // The rotation drag handle sits at the bottom edge of the unrotated markup and
    // rotates with it.  Compute how far the handle tip extends beyond the
    // bounding box on each vertical side so the toolbar doesn't overlap it.
    const handleTipY = (halfHeight + ROTATION_HANDLE_PX) * Math.cos(rotation);
    const handleOverflowBelow = Math.max(0, handleTipY - rotatedHalfHeight);
    const handleOverflowAbove = Math.max(0, -handleTipY - rotatedHalfHeight);

    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const isHidden =
      markupLeft + markupWidth < 0 ||
      markupLeft > containerWidth ||
      markupTop + markupHeight < 0 ||
      markupTop > containerHeight;

    // Visually hide the selection toolbar while the markup is off-screen.
    if (isHidden) {
      this.element.style.display = 'none';
    } else {
      this.element.style.display = 'flex';
    }

    // offsetWidth is accurate only once the element is in the DOM and visible.
    const toolbarWidth = this.element.offsetWidth || 225;

    // Horizontally center the toolbar with the markup.
    let left = markupLeft + markupWidth / 2 - toolbarWidth / 2;
    // Display the toolbar above the markup by default, with a small margin.
    let top = markupTop - handleOverflowAbove - TOOLBAR_HEIGHT_PX - TOOLBAR_MARGIN_PX;

    // Display the toolbar below the markup if there isn't enough room above it.
    // ...unless the markup spans the full container height, in which case we clamp to the top.
    if (top < TOOLBAR_MARGIN_PX) {
      const belowTop = markupTop + markupHeight + handleOverflowBelow + TOOLBAR_MARGIN_PX;
      if (belowTop + TOOLBAR_HEIGHT_PX + TOOLBAR_MARGIN_PX <= containerHeight) {
        top = belowTop;
      } else {
        top = TOOLBAR_MARGIN_PX;
      }
    }

    // Clamp to viewer bounds.
    left = Math.max(TOOLBAR_MARGIN_PX, Math.min(left, containerWidth - toolbarWidth - TOOLBAR_MARGIN_PX));
    top = Math.max(TOOLBAR_MARGIN_PX, Math.min(top, containerHeight - TOOLBAR_HEIGHT_PX - TOOLBAR_MARGIN_PX));

    this.element.style.left = `${left}px`;
    this.element.style.top = `${top}px`;
  }

  /** Hides the selection toolbar. */
  hide() {
    this.visible = false;
    this.element.style.display = 'none';
  }

  /** Removes the toolbar element from the DOM. Call when the markup extension is unloaded. */
  destroy() {
    this.visible = false;
    this.element.remove();
  }
}
